import "./TabConfigPage.scss";

import { Image, Text } from "@fluentui/react-northstar";
import { pages } from "@microsoft/teams-js";
import { randomString } from "@teams/client-shared";
import { TeamsApplicationTypes } from "@teams/types";
import React, { useEffect, useState } from "react";

import AddTabImage from "../../_shared/assets/add-tab.svg";
import AgileAddTabImage from "../../_shared/assets/agile-polly-add-tab.svg";
import { HeroUnit } from "../../_shared/components/HeroUnit";
import { channelApi } from "../services/ChannelApi";
import { useTeamsContext } from "../state/TeamsContextProvider";
import { getPageContext } from "../utils/teamsHelpers";

const PRIVATE_CHANNEL = "Private";

type TabConfigPageOptions = {
  urlBase: string;
  entityPrefix: string;
  displayName: string;
  title: string;
  subtitle?: string;
  footerText?: string;
  helpLink: string;
  applicationType: TeamsApplicationTypes;
};

export const TabConfigPage: React.FC<TabConfigPageOptions> = ({
  urlBase,
  entityPrefix,
  displayName,
  title,
  subtitle,
  footerText,
  helpLink,
  applicationType,
}) => {
  const { teamsContext } = useTeamsContext();
  const [errorValue, setErrorValue] = useState<string>();

  useEffect(() => {
    if (teamsContext.channel?.membershipType === PRIVATE_CHANNEL) {
      setErrorValue(
        "Applications currently do not work in Microsoft Teams private channels. Please try again in a public channel."
      );
      return;
    }

    if (!teamsContext?.team?.internalId && !teamsContext?.chat?.id) {
      setErrorValue(
        "Sorry, but this Polly tab does not work outside Team channels and group chats"
      );
      return;
    }

    pages.config.registerOnSaveHandler(event => {
      let url: string;

      //Is this a meeting?
      if (teamsContext.meeting?.id && getPageContext() === "/msteams") {
        url = `${window.location.origin}/msteams/content/meeting/tab?theme={theme}`;
      } else {
        url = `${window.location.origin}${urlBase}/content/tab/team?theme={theme}`;
      }

      const removeUrl = `${window.location.origin}${urlBase}/content/tabdelete?theme={theme}`;
      const entityId: string = `${entityPrefix}:${
        teamsContext?.channel?.id || teamsContext?.chat?.id
      }:${randomString()}`;
      pages.config.setConfig({
        entityId,
        contentUrl: url,
        websiteUrl: url,
        suggestedDisplayName: displayName,
        removeUrl,
      });

      channelApi
        .tabAdded(
          teamsContext,
          (teamsContext?.team?.internalId as string) || (teamsContext?.chat?.id as string),
          entityId,
          teamsContext?.channel?.id,
          teamsContext.meeting?.id || undefined
        )
        .finally(() => {
          event.notifySuccess();
        });
    });

    const validTabScope = (teamsContext.channel?.membershipType as string) !== PRIVATE_CHANNEL;
    const contextGroupId = teamsContext?.team?.internalId || teamsContext?.chat?.id;
    pages.config.setValidityState(validTabScope && !!contextGroupId);
  }, [urlBase, entityPrefix, displayName, teamsContext]);

  return (
    <div className="tab-config__container">
      {errorValue && <Text content={errorValue} size="larger" color="red" as="div" />}
      {!errorValue && (
        <div>
          <Text content={title} className="tab-config__title" as="div" />
          {subtitle && <Text content={subtitle} className="tab-config__subtitle" as="div" />}

          <HeroUnit style={{ padding: 10 }}>
            {applicationType === "msteams" && (
              <Image src={AddTabImage} className="tab-config__image" />
            )}
            {applicationType === "agile" && (
              <Image src={AgileAddTabImage} className="tab-config__image" />
            )}
          </HeroUnit>
          <Text
            content={footerText}
            className="tab-config__footer"
            as="div"
            styles={{ display: "inline" }}
          />
          <a className="tab-config__link" target="_blank" rel="noopener noreferrer" href={helpLink}>
            Learn more
          </a>
        </div>
      )}
    </div>
  );
};
